import { SeoPage, CtaInline } from './SeoLayout';
import { getLocalBusinessSchema, getBreadcrumbSchema } from '../../data/businessInfo';
import { Phone, MapPin, FileText, Paintbrush, Handshake } from 'lucide-react';

const STEPS = [
  {
    icon: Phone,
    num: '01',
    title: 'Anfrage',
    body: 'Sie rufen uns an oder buchen einen Besichtigungstermin über unseren Online-Kalender. Beim ersten Kontakt klären wir kurz Umfang und Ort — das dauert meist unter 5 Minuten.'
  },
  {
    icon: MapPin,
    num: '02',
    title: 'Besichtigung vor Ort',
    body: 'Wir kommen zum vereinbarten Termin bei Ihnen vorbei. Sie zeigen uns die Räume oder das Objekt, wir nehmen Maß, klären Farbwünsche und besprechen offene Punkte. Die Besichtigung ist kostenlos und unverbindlich.'
  },
  {
    icon: FileText,
    num: '03',
    title: 'Festpreis-Angebot',
    body: 'Innerhalb weniger Werktage erhalten Sie ein schriftliches Angebot mit klarem Festpreis, ausgewiesenen Arbeits- und Materialkosten sowie einem Zeitrahmen. Kein Klein-Gedrucktes, keine versteckten Kosten.'
  },
  {
    icon: Paintbrush,
    num: '04',
    title: 'Ausführung',
    body: 'Wir vereinbaren einen konkreten Starttermin und arbeiten dann konzentriert durch. Möbel decken wir ab, Böden schützen wir, und wir hinterlassen die Baustelle täglich aufgeräumt. Sie können jederzeit nachfragen — wir sind Ihr direkter Ansprechpartner.'
  },
  {
    icon: Handshake,
    num: '05',
    title: 'Übergabe & Rechnung',
    body: 'Zum Abschluss gehen wir gemeinsam durch die Räume und schauen, ob alles zu Ihrer Zufriedenheit passt. Kleine Nacharbeiten werden direkt erledigt. Anschließend erhalten Sie eine ordnungsgemäße Rechnung (Arbeitskosten separat ausgewiesen für Ihre Steuererklärung).'
  }
];

export default function SoArbeitenWirPage() {
  return (
    <SeoPage
      title="So läuft ein Auftrag ab | FarbWelt Maler Express Service Dormagen"
      description="In 5 klaren Schritten von der Anfrage bis zur Übergabe: Wie wir bei Ihnen in Dormagen und Umgebung Malerarbeiten planen, kalkulieren und ausführen — transparent, pünktlich, sauber."
      canonicalPath="/so-arbeiten-wir"
      kicker="Unser Ablauf"
      heroTitle="Von der Anfrage bis zur Übergabe — in fünf klaren Schritten"
      heroSubtitle="Damit Sie genau wissen, was Sie erwartet, wenn Sie uns beauftragen. Kein Rätselraten, kein Klein-Gedrucktes, kein Aufpreis am Wochenende."
      jsonLd={[
        getLocalBusinessSchema(),
        getBreadcrumbSchema([
          { name: 'Startseite', item: '/' },
          { name: 'So arbeiten wir', item: '/so-arbeiten-wir' }
        ])
      ]}
    >
      <div className="not-prose space-y-6">
        {STEPS.map((s, i) => (
          <div key={i} className="flex gap-5 sm:gap-8 items-start p-5 sm:p-7 bg-white border border-gray-200 rounded-xl hover:border-[#9B1B30]/40 transition-colors">
            <div className="flex-shrink-0">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-[#9B1B30]/10 flex items-center justify-center">
                <s.icon className="w-7 h-7 sm:w-8 sm:h-8 text-[#9B1B30]" />
              </div>
            </div>
            <div>
              <div className="text-xs font-bold text-[#9B1B30] tracking-widest mb-1">SCHRITT {s.num}</div>
              <h2 className="text-xl sm:text-2xl font-bold text-[#1A1A1A] mb-2 mt-0">{s.title}</h2>
              <p className="text-gray-700 leading-relaxed m-0">{s.body}</p>
            </div>
          </div>
        ))}
      </div>

      <CtaInline text="Bereit für Schritt 01?" />

      <h2>Was wir Ihnen dabei versprechen</h2>
      <ul>
        <li><strong>Festpreis nach Besichtigung</strong> — Sie wissen vorher, was der Auftrag kostet. Keine bösen Überraschungen.</li>
        <li><strong>Feste Ansprechpartner</strong> — Sie reden nicht mit Callcentern, sondern mit dem Team, das bei Ihnen arbeitet.</li>
        <li><strong>Saubere Baustelle</strong> — Wir decken sorgfältig ab, arbeiten staubarm und räumen täglich auf.</li>
        <li><strong>Pünktlich</strong> — Termine werden eingehalten. Sollte einmal etwas dazwischenkommen, informieren wir Sie sofort.</li>
        <li><strong>Ordentliche Rechnung</strong> — Steuerlich anerkannt (§ 35a EStG), Arbeitsleistung ausgewiesen.</li>
      </ul>

      <h2>Wie lange dauert ein Auftrag typischerweise?</h2>
      <p>
        Als grobe Orientierung — die tatsächliche Dauer klären wir immer beim Angebot:
      </p>
      <ul>
        <li>Einzelner Raum streichen (ca. 20 m²): 1 Arbeitstag</li>
        <li>2-Zimmer-Wohnung komplett renovieren: 2–4 Tage</li>
        <li>Fassade eines Einfamilienhauses: 3–6 Tage (witterungsabhängig)</li>
        <li>Tapete entfernen + neu tapezieren pro Raum: 1–2 Tage</li>
      </ul>
    </SeoPage>
  );
}
